/**
 * Recorrido del subárbol de una carpeta, para la descarga en ZIP.
 *
 * Reemplaza a loadAllFoldersRecursively.js, que (a) recorría el árbol con
 * `await` recursivo EN SERIE y (b) construía JSX (`Tag`, `FileFilled`,
 * `uuidv4`) que su único consumidor descartaba, quedándose solo con
 * `{ id, name }` para traducir rutas después. Aquí la ruta se construye
 * durante el recorrido, así que no hay traducción posterior que pueda fallar y
 * dejar el ZIP plano.
 *
 * Modelo de datos (fácil de equivocar): el doc self `D` de una carpeta tiene
 * `folders[]` y `files[]`. Para la entrada `E` de `D.folders[]`, la colección
 * hija `C_E = D.collection(E.reference.id)` contiene los DOCS-PUNTERO de los
 * archivos de E y, si E tiene subcarpetas, el doc self de E. Los metadatos de
 * esos archivos están en `D.files[]`, porque upload.js:80 escribe en
 * `C_E.parent` — que es D, no el self de E.
 *
 * Por eso los punteros son la AUTORIDAD sobre la pertenencia (viven en la
 * colección correcta) y el array es solo fuente de metadatos, filtrado por los
 * punteros presentes. Recorrer por el array pondría los archivos un nivel
 * arriba en el ZIP.
 *
 * Módulo puro: las referencias llegan por parámetro (basta `.get()` y
 * `.collection()`).
 */

import { FILES_SUBCOLLECTION, mergeFolderFiles } from './folder_files';
import { splitFolderDocs, buildFileRows, hydrateRows, FILE_HYDRATION_BATCH } from './folder_listing';

/**
 * Archivos de un subárbol, con su ruta relativa ya armada.
 *
 * @param {object} params
 * @param {object} params.parentDoc     DocumentReference del doc self PADRE
 *                                      (el que contiene folders[] y files[])
 * @param {string} params.collectionId  id de la colección hija de la carpeta a
 *                                      descargar (folder.reference.id)
 * @param {string} params.folderName    nombre de la carpeta a descargar; es el
 *                                      primer segmento del zipPath
 * @param {object} [params.rootEntry]   entrada folders[] de la carpeta descargada
 * @param {function} [params.filesAllowed] async ({ collectionPath, entries }) →
 *        boolean. Sus archivos entran solo si da true (spec 2026-10-06: «Leer»
 *        y «Descargar archivos»). El recorrido sigue bajando igual: una
 *        subcarpeta puede tener acceso propio. Sin filtro: todo.
 * @param {function} [params.pruneSubtree] async ({ collectionPath, entries }) →
 *        boolean. true = la carpeta y TODO su subárbol se saltan sin leerlos
 *        (spec 2026-10-07: carpeta restringida; las restricciones nunca se
 *        levantan hacia abajo). Si lanza o la carpeta no tiene ruta: se salta.
 * @param {number} [params.batchSize]
 * @returns {Promise<{ files: object[], folders: string[], reads: number, failed: number, skippedFolders: number, allowedFolders: number }>}
 *   `folders`: zipPath de cada carpeta recorrida (no podada y legible), tenga
 *   o no archivos; con ella el ZIP puede incluir las carpetas vacías.
 */
export const collectSubtreeFiles = async ({
  parentDoc, collectionId, folderName, rootEntry = null, filesAllowed = null, pruneSubtree = null, batchSize = FILE_HYDRATION_BATCH,
}) => {
  const files = [];
  const folders = [];
  let reads = 0;
  let failed = 0;
  let skippedFolders = 0;
  let allowedFolders = 0;

  // Entradas del array de un doc self (+ files-store si está en overflow).
  const entriesOf = async (docRef, data) => {
    let storeDocs = [];
    if (data?.filesOverflow === true && typeof docRef?.collection === 'function') {
      try {
        const s = await docRef.collection(FILES_SUBCOLLECTION).get();
        reads += 1;
        storeDocs = s.docs.map(d => d.data());
      } catch (e) {
        console.warn('No se pudo leer files-store:', e?.message);
        failed += 1;
      }
    }
    return mergeFolderFiles(data?.files, storeDocs);
  };

  const allowed = async (collectionPath, entries) => {
    if (!filesAllowed) return true;
    try { return !!(await filesAllowed({ collectionPath, entries })); } catch (e) { return false; }
  };

  /**
   * Procesa la carpeta `name`, cuya colección hija es `collId` bajo `selfRef`
   * (el doc self del padre, ya leído en `selfData`). `entries` = entradas
   * folders[] desde la carpeta descargada hasta esta.
   */
  const walk = async (selfRef, selfData, collId, name, basePath, entries) => {
    const zipPath = basePath ? `${basePath}/${name}` : name;
    const collectionPath = typeof selfRef?.path === 'string' ? `${selfRef.path}/${collId}` : null;
    if (pruneSubtree) {
      let prune = true;
      if (collectionPath) {
        try { prune = !!(await pruneSubtree({ collectionPath, entries })); } catch (e) { prune = true; }
      }
      if (prune) { skippedFolders += 1; return; }
    }
    let snap;
    try {
      snap = await selfRef.collection(collId).get();
      reads += 1;
    } catch (e) {
      console.warn(`No se pudo leer la carpeta ${zipPath}:`, e?.message);
      failed += 1;
      return;
    }
    folders.push(zipPath);

    const { folderDocs, pointerDocs } = splitFolderDocs(snap.docs);

    if (await allowed(collectionPath, entries)) {
      allowedFolders += 1;
      const rows = buildFileRows({ pointerDocs, arrayEntries: await entriesOf(selfRef, selfData) });

      rows.filter(r => r.entry).forEach(r => {
        files.push({ name: r.entry.name, url: r.entry.url, fileId: r.fileId, zipPath, ref: r.ref });
      });

      const h = await hydrateRows({
        rows: rows.filter(r => !r.entry),
        batchSize,
        onRow: (row) => {
          if (row.failed) return;
          files.push({ name: row.entry.name, url: row.entry.url, fileId: row.fileId, zipPath, ref: row.ref });
        },
      });
      reads += h.reads;
      failed += h.failed;
    } else {
      skippedFolders += 1;
    }

    // Cada doc self encontrado aquí describe las subcarpetas de esta carpeta.
    // Se bajan en paralelo por tandas, no en serie.
    const subTasks = [];
    for (const doc of folderDocs) {
      const data = doc.data();
      const docRef = doc.ref;
      if (!docRef || typeof docRef.collection !== 'function') continue;
      (data.folders || []).forEach(sub => {
        if (!sub?.reference?.id) return;
        subTasks.push(() => walk(docRef, data, sub.reference.id, sub.name, zipPath, [...entries, sub]));
      });
    }
    const runTask = (t) => t();
    for (let i = 0; i < subTasks.length; i += batchSize) {
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(subTasks.slice(i, i + batchSize).map(runTask));
    }
  };

  let rootData = null;
  try {
    const rootSnap = await parentDoc.get();
    reads += 1;
    rootData = rootSnap.data();
  } catch (e) {
    console.warn('No se pudo leer el doc padre de la carpeta:', e?.message);
    failed += 1;
  }

  await walk(parentDoc, rootData, collectionId, folderName, '', [rootEntry || { name: folderName, reference: { id: collectionId } }]);
  return { files, folders, reads, failed, skippedFolders, allowedFolders };
};

/**
 * Cuántas subcarpetas hay debajo de una carpeta (todos los niveles, sin
 * contarla a ella). Para la confirmación de «Restringir acceso». Una lectura
 * por carpeta del subárbol; los errores de lectura se propagan.
 */
export const countSubtreeFolders = async ({ parentDoc, collectionId, batchSize = FILE_HYDRATION_BATCH }) => {
  let count = 0;
  const walk = async (selfRef, collId) => {
    const snap = await selfRef.collection(collId).get();
    const { folderDocs } = splitFolderDocs(snap.docs);
    const tasks = [];
    folderDocs.forEach((doc) => {
      if (!doc.ref || typeof doc.ref.collection !== 'function') return;
      (doc.data().folders || []).forEach((sub) => {
        if (!sub?.reference?.id) return;
        count += 1;
        tasks.push(() => walk(doc.ref, sub.reference.id));
      });
    });
    for (let i = 0; i < tasks.length; i += batchSize) {
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(tasks.slice(i, i + batchSize).map((t) => t()));
    }
  };
  await walk(parentDoc, collectionId);
  return count;
};

export default collectSubtreeFiles;
